/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useRef, useState } from 'react';
import { motion, useMotionValue, useSpring, useTransform } from 'motion/react';

interface AuraButtonProps {
  children: React.ReactNode;
  onClick?: () => void;
  className?: string;
  glowColor?: string; // Default: #00F5D4 (Cyan)
  secondaryGlow?: string; // Default: #00BFA0 (Mint)
  roundedClass?: string; // Default: rounded-full
  paddingClass?: string; // Default: px-8 py-4
  variant?: 'primary' | 'secondary'; // 'primary' matches the high-fidelity tech-protocol style, 'secondary' is default sleek dark
}

export default function AuraButton({
  children,
  onClick,
  className = '',
  glowColor,
  secondaryGlow,
  roundedClass = 'rounded-full',
  paddingClass = 'px-8 py-4',
  variant = 'secondary',
}: AuraButtonProps) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [isHovered, setIsHovered] = useState(false);

  const isPrimary = variant === 'primary';
  const finalGlowColor = glowColor || '#00F5D4';
  const finalSecondaryGlow = secondaryGlow || (isPrimary ? '#B967FF' : '#00BFA0');

  // Real-time mouse coordinates relative to button container for under-glow tracking
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  // Smooth springs for fluid mouse glow tracking
  const springX = useSpring(mouseX, { stiffness: 160, damping: 22 });
  const springY = useSpring(mouseY, { stiffness: 160, damping: 22 });

  const handleMouseMove = (e: React.MouseEvent<HTMLButtonElement>) => {
    const button = buttonRef.current;
    if (!button) return;

    const rect = button.getBoundingClientRect();
    const localX = e.clientX - rect.left;
    const localY = e.clientY - rect.top;

    mouseX.set(localX);
    mouseY.set(localY);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  // Outer ambient under-glow (atmospheric glow leaking behind button)
  const hoverGlowBg = useTransform(
    [springX, springY],
    ([x, y]) => `radial-gradient(110px circle at ${x}px ${y}px, ${finalGlowColor}3C, ${finalSecondaryGlow}1E, transparent 100%)`
  );

  // Spotlight Internal Glow background mapping (for hover interaction inside secondary buttons)
  const internalGlowBg = useTransform(
    [springX, springY],
    ([x, y]) => `radial-gradient(100px circle at ${x}px ${y}px, ${finalGlowColor}25, ${finalSecondaryGlow}12, transparent 100%)`
  );

  return (
    <motion.button
      ref={buttonRef}
      onClick={onClick}
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      whileTap={{ scale: 0.98 }}
      className={`group relative p-[1.5px] overflow-visible flex items-center justify-center cursor-pointer transition-all duration-300 ${roundedClass} ${className} ${
        isHovered
          ? 'shadow-[0_0_35px_rgba(0,245,212,0.22)]'
          : 'shadow-[0_4px_25px_rgba(0,0,0,0.5)]'
      }`}
    >
      {/* 1. Outer Atmospheric Under-Glow (Halo effect leaking behind border) */}
      <motion.div
        className={`absolute -inset-[12px] blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none z-0 ${roundedClass}`}
        style={{
          background: hoverGlowBg,
        }}
      />

      {/* 2. Custom Border Design (Always present & rotating!) */}
      <div className={`absolute inset-0 pointer-events-none z-1 overflow-hidden ${roundedClass}`}>
        {/* Static subtle border base */}
        <div className={`absolute inset-0 bg-white/10 group-hover:bg-white/20 transition-colors duration-300 ${roundedClass}`} />

        {/* Rotating Conic Gradient Border Beam */}
        <motion.div
          className="absolute inset-[-250%]"
          style={{
            background: `conic-gradient(from 0deg, transparent 55%, ${finalGlowColor} 82%, ${finalSecondaryGlow} 94%, transparent 100%)`,
          }}
          animate={{ rotate: 360 }}
          transition={{
            repeat: Infinity,
            ease: 'linear',
            duration: isHovered ? 2 : 3.5,
          }}
        />
      </div>

      {/* 3. Deep black to deep high-tech space theme-teal gradient inner mask */}
      <div className={`absolute inset-[1.2px] z-2 ${
        isPrimary
          ? 'bg-gradient-to-r from-[#010B09] via-[#02241E] to-[#000F0D] animate-flow-gradient bg-[length:200%_auto]'
          : 'bg-[#0A0A0A] transition-colors duration-300 group-hover:bg-[#060606]'
      } ${roundedClass}`} />

      {/* 4. Interactive Internal Spotlight (illuminates inside button, subtle overlay on top of mask) */}
      {!isPrimary && (
        <motion.div
          className={`absolute inset-[1.2px] z-3 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-300 ${roundedClass}`}
          style={{
            background: internalGlowBg,
          }}
        />
      )}

      {/* 5. Static subtle glossy gradient overlay */}
      <div className={`absolute inset-[1.2px] z-4 bg-gradient-to-r from-white/[0.01] to-white/[0.03] ${roundedClass}`} />

      {/* 6. Button Content */}
      <span className={`${paddingClass} relative z-10 w-full h-full flex items-center justify-center gap-2 font-bold text-base tracking-widest transition-all duration-300 ${
        isPrimary
          ? 'text-white group-hover:text-[#00F5D4]'
          : 'text-white group-hover:text-[#00F5D4]'
      }`}>
        {children}
      </span>
    </motion.button>
  );
}
